import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import ExcelJS from 'exceljs';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  InputAdornment
} from '@mui/material';
import { FileDownload, Search } from '@mui/icons-material';
import './StockReport.css';

const StockReport = () => {
  // Session validation
  useSessionValidation();

  const { companyId, branchId, token, companyName, username } = useAuth();

  // Data states
  const [stockData, setStockData] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedItemName, setSelectedItemName] = useState('');

  // Date states
  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');

  useEffect(() => {
    if (companyId && token) {
      fetchItems();
    }
  }, [companyId, token]);

  const fetchItems = async () => {
    setLoading(true);
    setLoadingMessage('Fetching items...');
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      const allItems = (response.response_data || response || []).sort((a, b) =>
        (a.Name || '').localeCompare(b.Name || '')
      );
      setItems(allItems);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
    setLoading(false);
  };

  const fetchStockData = async () => {
    if (!fromDate || !toDate) {
      return;
    }

    setLoading(true);
    setLoadingMessage('Fetching stock report...');
    try {
      // Build API endpoint with item filter (0 for all items)
      const itemId = selectedItem || 0;
      const endpoint = `/Stock/${companyId}/${branchId}/${itemId}/${fromDate}/${toDate}`;

      const response = await fetchData('ReportApi', endpoint, token);
      const data = response.response_data || response || [];
      setStockData(data);
    } catch (error) {
      console.error('Error fetching stock report:', error);
      setStockData([]);
    }
    setLoading(false);
  };

  const handleItemChange = (event) => {
    const itemId = event.target.value;
    setSelectedItem(itemId);

    const item = items.find(i => i.Id === itemId);
    setSelectedItemName(item ? item.Name : '');

    // Clear previous data
    setStockData([]);
  };

  const handleGetReport = () => {
    fetchStockData();
  };

  // Filter items based on search
  const filteredItems = items.filter(item =>
    item.Name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  // Export to Excel
  const exportToExcel = async () => {
    if (stockData.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Stock Report');

    // Set column widths
    worksheet.columns = [
      { width: 10 },  // Sl No
      { width: 35 },  // Item Name
      { width: 15 },  // Opening Qty
      { width: 15 },  // Purchase Qty
      { width: 15 },  // Sale Qty
      { width: 15 },  // Sale Return Qty
      { width: 15 },  // Transfer In Qty
      { width: 15 },  // Transfer Out Qty
      { width: 15 }   // Closing Qty
    ];

    // Add Company Header
    const companyRow = worksheet.addRow([companyName || 'Company Name']);
    worksheet.mergeCells(`A1:I1`);
    companyRow.height = 28;
    companyRow.getCell(1).font = { bold: true, size: 16, color: { argb: 'FF1F4788' } };
    companyRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
    companyRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6EAF8' } };
    companyRow.getCell(1).border = {
      top: { style: 'medium', color: { argb: 'FF1F4788' } },
      bottom: { style: 'medium', color: { argb: 'FF1F4788' } },
      left: { style: 'medium', color: { argb: 'FF1F4788' } },
      right: { style: 'medium', color: { argb: 'FF1F4788' } }
    };

    // Add Title
    const titleText = selectedItemName 
      ? `Stock Report - ${selectedItemName}` 
      : 'Stock Report';
    const titleRow = worksheet.addRow([titleText]);
    worksheet.mergeCells(`A2:I2`);
    titleRow.height = 24;
    titleRow.getCell(1).font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } };
    titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
    titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B3F8F' } };

    // Add Period
    const periodRow = worksheet.addRow([`Period: ${formatDate(fromDate)} to ${formatDate(toDate)}`]);
    worksheet.mergeCells(`A3:I3`);
    periodRow.height = 20;
    periodRow.getCell(1).font = { size: 10, color: { argb: 'FF2C3E50' } };
    periodRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    periodRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };

    // Add Generated Time
    const generatedRow = worksheet.addRow([`Generated: ${new Date().toLocaleString('en-GB')}`]);
    worksheet.mergeCells(`A4:I4`);
    generatedRow.height = 20;
    generatedRow.getCell(1).font = { size: 10, color: { argb: 'FF2C3E50' } };
    generatedRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    generatedRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };

    // Add Generated By
    const generatedByRow = worksheet.addRow([`Generated By: ${username || 'User'}`]);
    worksheet.mergeCells(`A5:I5`);
    generatedByRow.height = 20;
    generatedByRow.getCell(1).font = { size: 10, color: { argb: 'FF2C3E50' } };
    generatedByRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    generatedByRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };

    // Empty row
    worksheet.addRow([]);

    // Add Table Headers
    const headerRow = worksheet.addRow([
      'Sl No',
      'Item Name',
      'Opening Qty',
      'Purchase Qty',
      'Sale Qty',
      'Sale Return Qty',
      'Transfer In Qty',
      'Transfer Out Qty',
      'Closing Qty'
    ]);
    headerRow.height = 24;

    // Apply styling to header cells
    for (let col = 1; col <= 9; col++) {
      const cell = headerRow.getCell(col);
      cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B3F8F' } };
      cell.border = {
        top: { style: 'medium', color: { argb: 'FF000000' } },
        bottom: { style: 'medium', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
        right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
      };
      if (col >= 3) {
        cell.alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
      } else {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
    }

    // Add Data Rows
    stockData.forEach((item, index) => {
      const dataRow = worksheet.addRow([
        index + 1,
        item.ItemName || '-',
        parseFloat(item.OpeningQty) || 0,
        parseFloat(item.PurchaseQty) || 0,
        parseFloat(item.SaleQty) || 0,
        parseFloat(item.SaleReturnQty) || 0,
        parseFloat(item.TransferInQty) || 0,
        parseFloat(item.TransferOutQty) || 0,
        parseFloat(item.ClosingQty) || 0
      ]);

      const bgColor = index % 2 === 0 ? 'FFFFFFFF' : 'FFF8F9FA';
      for (let col = 1; col <= 9; col++) {
        const cell = dataRow.getCell(col);
        cell.font = { size: 10 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFD5D8DC' } },
          bottom: { style: 'thin', color: { argb: 'FFD5D8DC' } },
          left: { style: 'thin', color: { argb: 'FFD5D8DC' } },
          right: { style: 'thin', color: { argb: 'FFD5D8DC' } }
        };
        if (col === 1) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else if (col === 2) {
          cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
        } else {
          cell.alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
          cell.numFmt = '#,##0.00';
        }
      }
    });

    // Calculate totals
    const totals = stockData.reduce((acc, item) => ({
      openingQty: acc.openingQty + (parseFloat(item.OpeningQty) || 0),
      purchaseQty: acc.purchaseQty + (parseFloat(item.PurchaseQty) || 0),
      saleQty: acc.saleQty + (parseFloat(item.SaleQty) || 0),
      saleReturnQty: acc.saleReturnQty + (parseFloat(item.SaleReturnQty) || 0),
      transferInQty: acc.transferInQty + (parseFloat(item.TransferInQty) || 0),
      transferOutQty: acc.transferOutQty + (parseFloat(item.TransferOutQty) || 0),
      closingQty: acc.closingQty + (parseFloat(item.ClosingQty) || 0)
    }), {
      openingQty: 0,
      purchaseQty: 0,
      saleQty: 0,
      saleReturnQty: 0,
      transferInQty: 0,
      transferOutQty: 0,
      closingQty: 0
    });

    // Add Total Row
    const totalRow = worksheet.addRow([
      '',
      'Total',
      totals.openingQty,
      totals.purchaseQty,
      totals.saleQty,
      totals.saleReturnQty,
      totals.transferInQty,
      totals.transferOutQty,
      totals.closingQty
    ]);

    for (let col = 1; col <= 9; col++) {
      const cell = totalRow.getCell(col);
      cell.font = { bold: true, size: 10 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8E8E8' } };
      cell.border = {
        top: { style: 'medium', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FFD5D8DC' } },
        right: { style: 'thin', color: { argb: 'FFD5D8DC' } }
      };
      if (col === 1) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      } else if (col === 2) {
        cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
      } else {
        cell.alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
        cell.numFmt = '#,##0.00';
      }
    }

    // Generate filename
    const filename = `Stock_Report_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Calculate totals
  const calculateTotals = () => {
    return stockData.reduce((acc, item) => ({
      openingQty: acc.openingQty + (parseFloat(item.OpeningQty) || 0),
      purchaseQty: acc.purchaseQty + (parseFloat(item.PurchaseQty) || 0),
      saleQty: acc.saleQty + (parseFloat(item.SaleQty) || 0),
      saleReturnQty: acc.saleReturnQty + (parseFloat(item.SaleReturnQty) || 0),
      transferInQty: acc.transferInQty + (parseFloat(item.TransferInQty) || 0),
      transferOutQty: acc.transferOutQty + (parseFloat(item.TransferOutQty) || 0),
      closingQty: acc.closingQty + (parseFloat(item.ClosingQty) || 0)
    }), {
      openingQty: 0,
      purchaseQty: 0,
      saleQty: 0,
      saleReturnQty: 0,
      transferInQty: 0,
      transferOutQty: 0,
      closingQty: 0
    });
  };

  const totals = calculateTotals();

  return (
    <div style={{
      minWidth: '100%',
      background: '#f5f7fa',
      padding: '10px',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        padding: '12px',
        position: 'relative',
        color: 'black',
        fontFamily: 'Times New Roman',
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          paddingBottom: '8px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div>
            <h4 style={{
              fontSize: '17px',
              fontWeight: '700',
              margin: 0,
              letterSpacing: '-0.3px',
              fontFamily: 'Times New Roman',
              textTransform: 'uppercase'
            }}>
              <span style={{ color: 'black', fontFamily: 'Times New Roman' }}>Stock Report</span>
            </h4>
          </div>
        </div>

        {/* Filter Section */}
        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-end',
          marginBottom: '12px',
          flexWrap: 'wrap'
        }}>
          {/* Item Dropdown */}
          <FormControl size="small" style={{ minWidth: '300px', flex: 1 }}>
            <InputLabel id="item-select-label" style={{ fontSize: '13px' }}>
              Select Item (Optional)
            </InputLabel>
            <Select
              labelId="item-select-label"
              value={selectedItem}
              onChange={handleItemChange}
              label="Select Item (Optional)"
              sx={{
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '35px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0f3460',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0f3460',
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              <MenuItem value="">
                <em>All Items</em>
              </MenuItem>
              <Box sx={{ px: 1, py: 0.5 }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search style={{ fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '12px',
                    },
                  }}
                />
              </Box>
              {filteredItems.map((item) => (
                <MenuItem key={item.Id} value={item.Id} style={{ fontSize: '13px' }}>
                  {item.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* From Date */}
          <TextField
            label="From Date"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              minWidth: '150px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '35px',
                '&:hover fieldset': {
                  borderColor: '#0f3460',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0f3460',
                },
              },
            }}
          />

          {/* To Date */}
          <TextField
            label="To Date"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              minWidth: '150px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '35px',
                '&:hover fieldset': {
                  borderColor: '#0f3460',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0f3460',
                },
              },
            }}
          />

          {/* Get Report Button */}
          <Button
            variant="contained"
            onClick={handleGetReport}
            disabled={!fromDate || !toDate}
            style={{
              background: fromDate && toDate
                ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
                : '#ccc',
              color: 'white',
              padding: '6px 20px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '700',
              boxShadow: fromDate && toDate
                ? '0 2px 8px rgba(102, 126, 234, 0.4)'
                : 'none',
              border: 'none',
              height: '35px',
              textTransform: 'none',
              letterSpacing: '0.5px',
            }}
          >
            Get Report
          </Button>

          {/* Export to Excel Button */}
          <Button
            variant="contained"
            onClick={exportToExcel}
            disabled={stockData.length === 0}
            startIcon={<FileDownload />}
            style={{
              background: stockData.length > 0
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : '#ccc',
              color: 'white',
              padding: '6px 20px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '700',
              boxShadow: stockData.length > 0
                ? '0 2px 8px rgba(16, 185, 129, 0.4)'
                : 'none',
              border: 'none',
              height: '35px',
              textTransform: 'none',
              letterSpacing: '0.5px',
            }}
          >
            Export to Excel
          </Button>
        </div>

        {/* Report Title */}
        {stockData.length > 0 && (
          <div style={{
            textAlign: 'center',
            marginBottom: '10px',
            padding: '6px',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            borderRadius: '6px',
            color: 'white'
          }}>
            <Typography variant="h6" style={{ fontSize: '13px', fontWeight: '600' }}>
              Stock Report{selectedItemName ? ` - ${selectedItemName}` : ''}
              {' '}({formatDate(fromDate)} to {formatDate(toDate)})
            </Typography>
          </div>
        )}

        {/* Stock Table */}
        {stockData.length > 0 && (
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="stock table">
                <TableHead>
                  <TableRow style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    height: '28px'
                  }}>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2px',
                      padding: '4px 8px',
                      textAlign: 'center',
                      width: '50px',
                      lineHeight: '1.2'
                    }}>
                      Sl No
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      minWidth: '180px',
                      lineHeight: '1.2'
                    }}>
                      Item Name
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2px',
                      padding: '4px 8px',
                      textAlign: 'right',
                      width: '100px',
                      lineHeight: '1.2'
                    }}>
                      Opening Qty
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2px',
                      padding: '4px 8px',
                      textAlign: 'right',
                      width: '110px',
                      lineHeight: '1.2'
                    }}>
                      Purchase Qty
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2px',
                      padding: '4px 8px',
                      textAlign: 'right',
                      width: '90px',
                      lineHeight: '1.2'
                    }}>
                      Sale Qty
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2px',
                      padding: '4px 8px',
                      textAlign: 'right',
                      width: '120px',
                      lineHeight: '1.2'
                    }}>
                      Sale Return Qty
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2px',
                      padding: '4px 8px',
                      textAlign: 'right',
                      width: '120px',
                      lineHeight: '1.2'
                    }}>
                      Transfer In Qty
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2px',
                      padding: '4px 8px',
                      textAlign: 'right',
                      width: '130px',
                      lineHeight: '1.2'
                    }}>
                      Transfer Out Qty
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2px',
                      padding: '4px 8px',
                      textAlign: 'right',
                      width: '100px',
                      lineHeight: '1.2'
                    }}>
                      Closing Qty
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockData.map((item, index) => (
                    <TableRow
                      key={index}
                      style={{
                        background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                        borderBottom: '1px solid #e0e0e0',
                        height: '32px'
                      }}
                    >
                      <TableCell style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        color: '#2d3748',
                        textAlign: 'center',
                        lineHeight: '1.2'
                      }}>
                        {index + 1}
                      </TableCell>
                      <TableCell style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        color: '#2d3748',
                        textAlign: 'left',
                        lineHeight: '1.2'
                      }}>
                        {item.ItemName || '-'}
                      </TableCell>
                      <TableCell style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        color: '#2d3748',
                        textAlign: 'right',
                        lineHeight: '1.2'
                      }}>
                        {(parseFloat(item.OpeningQty) || 0).toFixed(2)}
                      </TableCell>
                      <TableCell style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        color: '#2d3748',
                        textAlign: 'right',
                        lineHeight: '1.2'
                      }}>
                        {(parseFloat(item.PurchaseQty) || 0).toFixed(2)}
                      </TableCell>
                      <TableCell style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        color: '#2d3748',
                        textAlign: 'right',
                        lineHeight: '1.2'
                      }}>
                        {(parseFloat(item.SaleQty) || 0).toFixed(2)}
                      </TableCell>
                      <TableCell style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        color: '#2d3748',
                        textAlign: 'right',
                        lineHeight: '1.2'
                      }}>
                        {(parseFloat(item.SaleReturnQty) || 0).toFixed(2)}
                      </TableCell>
                      <TableCell style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        color: '#2d3748',
                        textAlign: 'right',
                        lineHeight: '1.2'
                      }}>
                        {(parseFloat(item.TransferInQty) || 0).toFixed(2)}
                      </TableCell>
                      <TableCell style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        color: '#2d3748',
                        textAlign: 'right',
                        lineHeight: '1.2'
                      }}>
                        {(parseFloat(item.TransferOutQty) || 0).toFixed(2)}
                      </TableCell>
                      <TableCell style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        color: '#2d3748',
                        textAlign: 'right',
                        lineHeight: '1.2'
                      }}>
                        {(parseFloat(item.ClosingQty) || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total Row */}
                  <TableRow style={{
                    background: '#f0f0f0',
                    borderTop: '2px solid #0f3460',
                    height: '32px'
                  }}>
                    <TableCell colSpan={2} style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      color: '#2d3748',
                      textAlign: 'left',
                      fontWeight: '700',
                      lineHeight: '1.2'
                    }}>
                      Total
                    </TableCell>
                    <TableCell style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      color: '#2d3748',
                      textAlign: 'right',
                      fontWeight: '700',
                      lineHeight: '1.2'
                    }}>
                      {totals.openingQty.toFixed(2)}
                    </TableCell>
                    <TableCell style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      color: '#2d3748',
                      textAlign: 'right',
                      fontWeight: '700',
                      lineHeight: '1.2'
                    }}>
                      {totals.purchaseQty.toFixed(2)}
                    </TableCell>
                    <TableCell style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      color: '#2d3748',
                      textAlign: 'right',
                      fontWeight: '700',
                      lineHeight: '1.2'
                    }}>
                      {totals.saleQty.toFixed(2)}
                    </TableCell>
                    <TableCell style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      color: '#2d3748',
                      textAlign: 'right',
                      fontWeight: '700',
                      lineHeight: '1.2'
                    }}>
                      {totals.saleReturnQty.toFixed(2)}
                    </TableCell>
                    <TableCell style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      color: '#2d3748',
                      textAlign: 'right',
                      fontWeight: '700',
                      lineHeight: '1.2'
                    }}>
                      {totals.transferInQty.toFixed(2)}
                    </TableCell>
                    <TableCell style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      color: '#2d3748',
                      textAlign: 'right',
                      fontWeight: '700',
                      lineHeight: '1.2'
                    }}>
                      {totals.transferOutQty.toFixed(2)}
                    </TableCell>
                    <TableCell style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      color: '#2d3748',
                      textAlign: 'right',
                      fontWeight: '700',
                      lineHeight: '1.2'
                    }}>
                      {totals.closingQty.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}

        {/* No Data Message */}
        {!loading && fromDate && toDate && stockData.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666'
          }}>
            <Typography variant="body1">
              No stock data found for the selected period.
            </Typography>
          </div>
        )}

        {/* Initial Message */}
        {!fromDate || !toDate ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666'
          }}>
            <Typography variant="body1">
              Please select date range to view the stock report.
            </Typography>
          </div>
        ) : null}
      </div>

      <LoadingOverlay open={loading} message={loadingMessage} />
    </div>
  );
};

export default StockReport;
